'use client';

import { analyticsEventEmitter } from '@portals/wb-front-analytics-tracker';
import { currentZone } from '@portal-root/geo-utils';
import { TSendEventParams } from '@portals/wb-front-analytics-tracker/lib/emitter';
import { useCallback, useEffect } from 'react';
import { APP_NAMESPACE } from '@/_constants/app-namespace';
import { APP_VERSION } from '@/_constants/analytics';
import { ECountries } from '@/_types';
import { useClientEnvContext } from '@/contexts';
import { useLocaleCountry } from './use-locale-country';

type TOutput = ({ name, params }: TParams) => void;

type TEventParam = string | boolean | number | undefined;

type TParams = {
  name: string;
  params?: Record<string, TEventParam>;
};

const COUNTER_ID_STAGE = 27;
const COUNTER_ID_PROD = 7;

export const useAnalytics = (): TOutput => {
  const { country, locale } = useLocaleCountry();
  const { NEXT_PUBLIC_RUNTIME_ENV } = useClientEnvContext();

  const counterId =
    NEXT_PUBLIC_RUNTIME_ENV === 'prod' ? COUNTER_ID_PROD : COUNTER_ID_STAGE;

  useEffect(() => {
    if (
      !analyticsEventEmitter.getIsInitialized() &&
      country !== ECountries.TR &&
      counterId
    ) {
      analyticsEventEmitter.init({ counterId, cookieDomain: currentZone });
    }
  }, [counterId, country]);

  const sendEvent = useCallback(
    ({ name, params }: TParams): void => {
      const config: TSendEventParams = {
        name,
        params: {
          appVersion: APP_VERSION,
          language: locale,
          country,
          ...params,
        },
        projectName: APP_NAMESPACE,
        customEvent: true,
      };

      analyticsEventEmitter.sendEvent(config);
    },
    [locale, country],
  );

  return sendEvent;
};
